'use client'

import { motion } from 'framer-motion'
import {
  Zap,
  Shield,
  Clock,
  Wallet,
  Bot,
  Users,
  TrendingUp,
  Award,
  Sparkles,
  Globe,
  FileCheck,
  Rocket,
} from 'lucide-react'

const features = [
  {
    icon: Bot,
    title: 'AI Travel Agent',
    description: 'Your personal AI finds, applies, and negotiates the best contracts for you automatically.',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: Zap,
    title: 'Instant Matching',
    description: 'Get matched with perfect opportunities in minutes, not days. Our AI knows exactly what you need.',
    color: 'from-yellow-500 to-orange-500',
  },
  {
    icon: Clock,
    title: '2-3 Hour Credentialing',
    description: 'Upload once, work anywhere. Our AI handles all credentialing and compliance automatically.',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Wallet,
    title: 'Same-Day Pay',
    description: 'Get paid instantly after your shift. No more waiting weeks for your hard-earned money.',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: Award,
    title: 'Loyalty Rewards',
    description: 'Earn points for every hour worked. Redeem for cash, travel, CEUs, and exclusive perks.',
    color: 'from-pink-500 to-rose-500',
  },
  {
    icon: Users,
    title: 'Direct Marketplace',
    description: 'Connect directly with hospitals. No recruiter bottlenecks, no ghosting, just transparency.',
    color: 'from-indigo-500 to-purple-500',
  },
  {
    icon: TrendingUp,
    title: 'Live Rate Index',
    description: 'See real-time market rates for any location or specialty. Know your worth, always.',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: FileCheck,
    title: 'One-Time Credentialing Vault',
    description: 'Upload your credentials once. AI formats and shares with any hospital instantly.',
    color: 'from-orange-500 to-red-500',
  },
  {
    icon: Globe,
    title: 'Niche Specialties',
    description: 'ICU, Cardiovascular, Neuro, Trauma - we specialize in connecting specialized nurses.',
    color: 'from-teal-500 to-green-500',
  },
  {
    icon: Shield,
    title: 'Full Transparency',
    description: 'Fixed $8/hr margin. You always know what hospitals pay and what you earn.',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: Rocket,
    title: 'Strike-Ready Teams',
    description: 'Deploy specialized rapid-response teams for urgent hospital needs within hours.',
    color: 'from-red-500 to-pink-500',
  },
  {
    icon: Sparkles,
    title: 'Zero BS Experience',
    description: 'Automated everything. No more chasing recruiters, filling forms, or waiting on hold.',
    color: 'from-amber-500 to-yellow-500',
  },
]

export default function Features() {
  return (
    <section id="tech" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-2 glass-effect rounded-full mb-6"
          >
            <Sparkles className="w-5 h-5 text-yellow-400" fill="currentColor" />
            <span className="text-sm font-semibold text-gray-200">
              Next-Generation Features
            </span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why We&apos;re <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">Different</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We&apos;re not just another staffing agency. We&apos;re a complete AI-powered platform
            built to give nurses more money, more freedom, and less hassle.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="glass-effect rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group cursor-pointer"
            >
              <motion.div
                className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-4`}
                whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <feature.icon className="w-6 h-6 text-white" />
              </motion.div>

              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gradient group-hover:from-accent-400 group-hover:to-primary-400 transition-all">
                {feature.title}
              </h3>
              <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                {feature.description}
              </p>

              {/* Hover effect line */}
              <div className="mt-4 h-1 w-0 bg-gradient-to-r from-accent-400 to-primary-400 group-hover:w-full transition-all duration-300 rounded-full" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.button
            className="px-8 py-4 bg-gradient-to-r from-accent-500 to-primary-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-accent-500/50 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Experience the Future - Join Now
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}
